import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	type Carrier,
	getFetchReviewQueryKey,
	type ReviewResponse,
	useSaveReview,
} from '@uturn/api/v2';
import { queryClient } from '@uturn/portal/query';
import type { CarrierReview } from '@uturn/portal/types/shipment';
import { getApiErrorMessage } from '@uturn/portal/utils';
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Ratings,
	SheetClose,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	Stars,
	sonner,
	Textarea,
} from '@uturn/ui-kit';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
	type CarrierReviewFormValues,
	useCarrierReviewFormSchema,
} from './review-carrier-schema';

const useLabels = () => {
	const { t } = useTranslation();
	return {
		rating: t(
			'pages.order.shipment-details.assigned-carrier.review.form.rating',
			'Rating',
		),
		comment: t(
			'pages.order.shipment-details.assigned-carrier.review.form.comment',
			'Comment',
		),
		createdAt: t(
			'pages.order.shipment-details.assigned-carrier.review.form.create_at',
			'Date reviewed',
		),
	};
};

export function CarrierReviewForm({
	shipmentNumber,
}: {
	shipmentNumber: number;
}) {
	const { t } = useTranslation();
	const labels = useLabels();

	const formSchema = useCarrierReviewFormSchema(labels);

	const form = useForm<CarrierReviewFormValues>({
		resolver: zodResolver(formSchema),
		mode: 'onChange',
		defaultValues: {
			rating: 0,
			comment: '',
		},
	});

	const {
		formState: { isValid },
	} = form;

	const closeRef = useRef<HTMLButtonElement>(null);

	const saveReview = useSaveReview({
		mutation: {
			// Defined on the hook (not passed to mutate) so they still fire even if
			// the form unmounts before the request settles.
			onSuccess: () => {
				sonner.success(
					t(
						'pages.order.shipment-details.assigned-carrier.review.submit.success',
						'Review saved',
					),
				);

				queryClient.invalidateQueries({
					queryKey: getFetchReviewQueryKey(shipmentNumber),
				});

				// Close the sheet on success; avoids the re-submit gap while the
				// review query refetches and swaps in the details view.
				closeRef.current?.click();
			},
			onError: (error) => {
				const message = getApiErrorMessage(error);

				sonner.error(
					message ??
						t(
							'pages.order.shipment-details.assigned-carrier.review.submit.error',
							'Review could not be saved',
						),
				);
			},
		},
	});

	const onSubmit = (formData: CarrierReviewFormValues) => {
		saveReview.mutate({
			id: shipmentNumber,
			data: formData,
		});
	};

	return (
		<div className="col-span-full flex flex-col gap-1">
			{/* Hidden close target, triggered programmatically on successful save. */}
			<SheetClose ref={closeRef} className="hidden" />
			<h3 className="text font-heading font-medium tracking-tight">
				{t(
					'pages.order.shipment-details.assigned-carrier.review.form.heading',
					'Leave a review',
				)}
			</h3>
			<p className="pb-2 text-sm leading-6">
				{t(
					'pages.order.shipment-details.assigned-carrier.review.form.subheading',
					'UTURN backoffice will evaluate reviews and potentially forward them to the carrier. A review can only be added once for each shipment.',
				)}
			</p>
			<Form {...form}>
				<form
					onSubmit={(e) => {
						e.stopPropagation();
						return form.handleSubmit(onSubmit)(e);
					}}
					className="space-y-4"
				>
					<FormField
						control={form.control}
						name="rating"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{labels.rating}</FormLabel>
								<FormControl>
									{/*
									 * Not really sure why spreading field here works since the Ratings component does not have any callbacks.
									 * should probably add a callback function to the Ratings component, but this fixes the issue for now
									 */}
									<Ratings {...field} value={field.value ?? 0} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="comment"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{labels.comment}</FormLabel>
								<FormControl>
									<Textarea {...field} rows={6} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<div className="flex justify-end gap-3 sm:col-span-full">
						<SheetClose asChild>
							<Button
								size="sm"
								variant="secondary"
								disabled={saveReview.isPending}
							>
								{t(
									'pages.order.shipment-details.assigned-carrier.review.form.cancel',
									'Cancel',
								)}
							</Button>
						</SheetClose>
						<Button
							type="submit"
							size="sm"
							disabled={!isValid || saveReview.isPending}
						>
							{saveReview.isPending ? (
								<>
									<Icon className="mr-2 size-4" icon={faSpinnerThird} spin />
									{t(
										'pages.order.shipment-details.assigned-carrier.review.form.loading',
										'Saving...',
									)}
								</>
							) : (
								t(
									'pages.order.shipment-details.assigned-carrier.review.form.submit',
									'Send review',
								)
							)}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
}

export function CarrierReviewDetails({
	carrierReview,
}: {
	carrierReview: ReviewResponse;
}) {
	const { t } = useTranslation();
	const labels = useLabels();
	return (
		<div className="col-span-full flex flex-col gap-1">
			<h3 className="text font-heading pb-2 font-medium tracking-tight">
				{t(
					'pages.order.shipment-details.assigned-carrier.review.details.title',
					'Review',
				)}
			</h3>
			<p className="text-muted-foreground pb-2 text-sm leading-6">
				{t(
					'pages.order.shipment-details.assigned-carrier.review.form.subheading',
					'UTURN backoffice will evaluate reviews and potentially forward them to the carrier. A review can only be added once for each shipment.',
				)}
			</p>
			<dl className="pb-2">
				<dt>{labels.rating}</dt>
				<dd>
					<Stars rating={Math.round(carrierReview.rating)} />
				</dd>
			</dl>
			{carrierReview.comment && (
				<dl className="pb-5">
					<dt>{labels.comment}</dt>
					<dd>
						<Textarea disabled rows={6}>
							{carrierReview.comment}
						</Textarea>
					</dd>
				</dl>
			)}
			<div className="flex justify-end sm:col-span-full">
				<SheetClose asChild>
					<Button size="sm" variant="secondary">
						{t(
							'pages.order.shipment-details.assigned-carrier.review.details.close',
							'Close',
						)}
					</Button>
				</SheetClose>
			</div>
		</div>
	);
}

export function ReviewCarrierSheet({
	shipmentNumber,
	carrier,
	carrierReview,
}: {
	shipmentNumber: number;
	carrier: Carrier;
	carrierReview: CarrierReview;
}) {
	const { t } = useTranslation();

	return (
		<>
			<SheetHeader>
				<SheetTitle>
					{t(
						'pages.order.shipment-details.assigned-carrier.review.heading',
						'Review carrier',
					)}
				</SheetTitle>
				<SheetDescription>
					{t(
						'pages.order.shipment-details.assigned-carrier.review.subheading',
						'Review the carrier on the Uturn platform.',
					)}
				</SheetDescription>
			</SheetHeader>

			<div className="grid grid-cols-1 gap-x-6 gap-y-4 pt-4 sm:max-w-xl sm:grid-cols-6">
				<div className="col-span-full flex flex-col gap-1">
					<h3 className="text font-heading font-medium tracking-tight">
						{carrier.name}
					</h3>
				</div>
				{!carrierReview ? (
					<CarrierReviewForm shipmentNumber={shipmentNumber} />
				) : (
					<CarrierReviewDetails carrierReview={carrierReview} />
				)}
			</div>
		</>
	);
}
